import {
  APIComponentAttributes,
  APIFieldAttributes,
  APIReferenceAttributes,
  ArdoqId,
  GroupingRule,
  GroupType,
  ReferenceDirection,
} from '@ardoq/api-types';

export const isTiedToWorkspace = ({
  type,
}: Pick<GroupingRule, 'type'>): boolean => {
  switch (type) {
    case GroupType.REFERENCE:
    case GroupType.REFERENCE_CONTEXTUAL:
    case GroupType.PARENT:
    case GroupType.CHILD:
    case GroupType.FIELD:
    case GroupType.TAG:
      return true;
  }
  return false;
};

export const getWorkspaceId = (
  attributes: Pick<GroupingRule, 'type' | 'workspaceId'>,
  fallbackWorkspaceId: ArdoqId | null
): ArdoqId | undefined => {
  if (!isTiedToWorkspace(attributes)) {
    return undefined;
  }
  return attributes.workspaceId || fallbackWorkspaceId || undefined;
};

export const enrichGroupingRuleWithWorkspaceId = (
  rule: GroupingRule,
  contextWorkspaceId: ArdoqId | null
): GroupingRule => ({
  ...rule,
  workspaceId: getWorkspaceId(rule, contextWorkspaceId),
});

export const encodeGroupByToURI = (
  rule: Pick<
    GroupingRule,
    'type' | 'targetId' | 'workspaceId' | 'direction' | 'sublabel'
  >
): string => {
  const compactParams: {
    g?: string;
    t?: string;
    wid?: string;
    d?: string;
    sl?: string;
  } = {
    g: shorthandTypes[rule.type as GroupType],
  };
  if (isTiedToWorkspace(rule)) {
    compactParams.t = rule.targetId;
    compactParams.wid = rule.workspaceId;
  }
  if (rule.type === GroupType.REFERENCE_TYPE_NAME) {
    compactParams.d = rule.direction?.charAt(0);
    compactParams.t = rule.targetId;
  }
  if (
    rule.type === GroupType.REFERENCE ||
    rule.type === GroupType.REFERENCE_CONTEXTUAL
  ) {
    compactParams.d = rule.direction?.charAt(0);
  }
  if (rule.sublabel) {
    compactParams.sl = rule.sublabel;
  }
  return `${GROUPBY_MAP_PREFIX}${window.encodeURI(
    JSON.stringify(compactParams)
  )}&`;
};

const GROUPBY_MAP_PREFIX = 'g_m';

const shorthandTypes: Partial<Record<GroupType, string>> = {
  [GroupType.REFERENCE]: 'r',
  [GroupType.REFERENCE_TYPE_NAME]: 'rt',
  [GroupType.REFERENCE_CONTEXTUAL]: 'rc',
  [GroupType.COMPONENT]: 'ct',
  [GroupType.PARENT]: 'p',
  [GroupType.PARENT_ALL]: 'pa',
  [GroupType.CHILD]: 'c',
  [GroupType.FIELD]: 'f',
  [GroupType.TAG]: 't',
  [GroupType.WORKSPACE]: 'ws',
};

const getFullType = (shorthandType: string): GroupType | undefined =>
  Object.keys(shorthandTypes).find(
    key => shorthandTypes[key as GroupType] === shorthandType
  ) as GroupType | undefined;

const legacyParseUriEncodedGroupBy = (
  encoded: string
): Partial<GroupingRule> => {
  const parser = {
    regexp:
      /g_([a-z]{1,2})(?:_t_(.+?))?_dir_(incoming|outgoing)(?:_wid_([a-z0-9]+))?/i,
    shorthandType: 1,
    targetId: 2,
    direction: 3,
    workspaceId: 4,
  };
  const match = parser.regexp.exec(encoded);
  if (!match) return {};
  return {
    workspaceId: match[parser.workspaceId],
    targetId: match[parser.targetId],
    type: getFullType(match[parser.shorthandType]),
    direction: match[parser.direction] as ReferenceDirection | undefined,
  };
};

export const parseUriEncodedGroupBy = (
  encoded: string
): Partial<GroupingRule> => {
  if (!encoded.startsWith(GROUPBY_MAP_PREFIX)) {
    return legacyParseUriEncodedGroupBy(encoded);
  }
  const compact = JSON.parse(encoded.slice(GROUPBY_MAP_PREFIX.length));
  return {
    type: getFullType(compact.g),
    workspaceId: compact.wid,
    targetId: compact.t,
    direction: compact.d
      ? compact.d === 'i'
        ? ReferenceDirection.INCOMING
        : ReferenceDirection.OUTGOING
      : ReferenceDirection.UNSET,
    sublabel: compact.sl,
  };
};

const REQUIRED_ATTRS: Partial<
  Record<GroupType, ('targetId' | 'direction' | 'workspaceId')[]>
> = {
  [GroupType.REFERENCE]: ['targetId', 'direction', 'workspaceId'],
  [GroupType.REFERENCE_TYPE_NAME]: ['targetId', 'direction'],
  [GroupType.REFERENCE_CONTEXTUAL]: ['targetId', 'direction', 'workspaceId'],
  [GroupType.PARENT]: ['targetId', 'workspaceId'],
  [GroupType.CHILD]: ['targetId', 'workspaceId'],
  [GroupType.FIELD]: ['targetId', 'workspaceId'],
  [GroupType.TAG]: ['targetId', 'workspaceId'],
};

export const isValidRule = (rule: GroupingRule): boolean => {
  const { type } = rule;
  if (!type) return false;
  const requiredAttrs = REQUIRED_ATTRS[type] ?? [];
  return requiredAttrs.every(attr => Boolean(rule[attr]));
};

export const getValidRules = (rules: GroupingRule[]): GroupingRule[] =>
  rules.filter(isValidRule);

export const hasGroupByParentAll = (rules: GroupingRule[]): boolean =>
  rules.some(rule => rule.type === GroupType.PARENT_ALL);

export const hasGroupByParent = (
  rules: GroupingRule[],
  comp: APIComponentAttributes
): boolean =>
  Boolean(comp.rootWorkspace) &&
  rules.some(
    rule =>
      rule.type === GroupType.PARENT &&
      rule.workspaceId === comp.rootWorkspace &&
      rule.targetId === comp.typeId
  );

export const hasGroupByChild = (
  rules: GroupingRule[],
  comp: APIComponentAttributes
): boolean =>
  Boolean(comp.rootWorkspace) &&
  rules.some(
    rule =>
      rule.type === GroupType.CHILD &&
      rule.workspaceId === comp.rootWorkspace &&
      rule.targetId === comp.typeId
  );

export const hasGroupByComponentType = (rules: GroupingRule[]): boolean =>
  rules.some(rule => rule.type === GroupType.COMPONENT);

// NOTE: prior call sites passed a workspaceId here, but the Backbone
// implementation took no args and returned true whenever *any* WORKSPACE
// rule was active. The dead arg is dropped at the signature level now.
export const hasGroupByWorkspace = (rules: GroupingRule[]): boolean =>
  rules.some(rule => rule.type === GroupType.WORKSPACE);

export const hasGroupByOutgoingReference = (
  rules: GroupingRule[],
  ref: APIReferenceAttributes
): boolean =>
  rules.some(
    rule =>
      rule.type === GroupType.REFERENCE &&
      rule.workspaceId === ref.rootWorkspace &&
      rule.targetId === `${ref.type}` &&
      rule.direction === ReferenceDirection.OUTGOING
  );

export const hasGroupByIncomingReference = (
  rules: GroupingRule[],
  ref: APIReferenceAttributes
): boolean =>
  rules.some(
    rule =>
      rule.type === GroupType.REFERENCE &&
      rule.workspaceId === ref.rootWorkspace &&
      rule.targetId === `${ref.type}` &&
      rule.direction === ReferenceDirection.INCOMING
  );

export const hasGroupByTag = (
  rules: GroupingRule[],
  tag: { id: string }
): boolean =>
  rules.some(rule => rule.type === GroupType.TAG && rule.targetId === tag.id);

export const hasGroupByField = (
  rules: GroupingRule[],
  field: APIFieldAttributes
): boolean =>
  rules.some(
    rule => rule.type === GroupType.FIELD && rule.targetId === field._id
  );

export const toGlobalTargetId = (groupingRule: GroupingRule): GroupingRule => {
  switch (groupingRule.type) {
    case GroupType.REFERENCE:
    case GroupType.REFERENCE_CONTEXTUAL: {
      const targetId = `${groupingRule.workspaceId}-${groupingRule.targetId}`;
      return { ...groupingRule, targetId };
    }
  }
  return { ...groupingRule };
};
